import { useEffect, useEffectEvent } from 'react';
import type { FlowSummary } from '@/lib/api/email-flows/types';
import { useEmailQuery, useFlowQuery } from '../queries/useEmailQueries';
import { useEmailTemplate } from './useEmailTemplate';
import { useEmailMetadata } from './useEmailMetadata';
import { useEmailEditorStore } from '../stores';

interface UseEmailEditorDataProps {
    flowId: number;
    emailId?: number;
    initialFlowData?: FlowSummary;
}

/**
 * Hook that fetches email editor data and populates the store.
 * This should be called once at the top level (EmailEditorLayout or page level).
 */
export function useEmailEditorData({ flowId, emailId, initialFlowData }: UseEmailEditorDataProps) {
    const setFlowData = useEmailEditorStore(state => state.setFlowData);
    const setEmailData = useEmailEditorStore(state => state.setEmailData);
    const setTemplateData = useEmailEditorStore(state => state.setTemplateData);
    const setMetadata = useEmailEditorStore(state => state.setMetadata);

    // Event handlers that always use latest store setters without being dependencies
    const updateFlowData = useEffectEvent((data: FlowSummary) => {
        setFlowData(data);
    });

    const updateEmailData = useEffectEvent((data: any, id?: number) => {
        if (id) {
            setEmailData(data || null);
        } else {
            setEmailData(null);
        }
    });

    const updateTemplateData = useEffectEvent((variables: any, defaults: any, values: any) => {
        setTemplateData(variables, defaults, values);
    });

    const updateMetadata = useEffectEvent((meta: any) => {
        setMetadata({
            trigger: meta.trigger,
            isFirstTimeBasedEmail: meta.isFirstTimeBasedEmail,
            isCartAbandonedFlow: meta.isCartAbandonedFlow,
            actionsDisabled: meta.actionsDisabled,
            hasSentEmails: meta.hasSentEmails,
            triggerEventName: meta.triggerEventName,
            canAddAnotherEmail: meta.canAddAnotherEmail,
        });
    });

    // Fetch flow data
    const { data: flowData } = useFlowQuery({
        flowId,
        initialData: initialFlowData,
    });

    // Fetch email data only when emailId is valid (editing existing email)
    // For Add Email page, pass a dummy value but query won't run due to enabled: emailId > 0
    const { data: emailData } = useEmailQuery({ emailId: emailId || 0 });

    // Get metadata
    const metadata = useEmailMetadata({
        flowData,
        emailData: emailId ? emailData : undefined,
        emailId,
    });

    // Get template data
    const { templateVariables, templateDefaults, defaultValues } = useEmailTemplate({
        flowData,
        emailData: emailId ? emailData : undefined,
        emailId,
    });

    // Update store when flow data changes
    useEffect(() => {
        if (flowData) {
            updateFlowData(flowData);
        }
    }, [flowData]);

    // Update store when email data changes
    useEffect(() => {
        updateEmailData(emailData, emailId);
    }, [emailId, emailData]);

    // Update store when template data changes
    // Only re-runs when actual template data changes
    useEffect(() => {
        updateTemplateData(templateVariables, templateDefaults, defaultValues);
    }, [templateVariables, templateDefaults, defaultValues]);

    // Update store when metadata changes
    // Only re-runs when metadata object changes
    useEffect(() => {
        updateMetadata(metadata);
    }, [metadata]);

    return {
        flowData,
        emailData,
        isLoading: !flowData,
    };
}
